import type {
  ListingHealth,
  ListingHealthIssue,
  ListingHealthIssueCode,
  ListingHealthIssueKind,
  ListingHealthIssueParams
} from "types/landlord/address";

const UNIT_SECTION = {
  General: "General",
  Amenities: "Amenities",
  Plan: "Plan",
  RentalInformation: "Rental information",
  LeaseTerms: "Lease terms, utilities & policies",
  Photos: "Photos",
  ListingAppearance: "Listing appearance"
} as const;

const PROPERTY_SECTION = {
  Photos: "Photos",
  Fees: "Fees",
  Branding: "Branding",
  General: "General"
} as const;

type UnitSection = (typeof UNIT_SECTION)[keyof typeof UNIT_SECTION];
type PropertySection = (typeof PROPERTY_SECTION)[keyof typeof PROPERTY_SECTION];

/** A unit section is on the Unit page; a property one opens the Property page. */
export type ListingHealthFix =
  | { target: "unit"; section: UnitSection }
  | { target: "property"; section: PropertySection };

export interface ListingHealthLine {
  code: ListingHealthIssueCode;
  kind: ListingHealthIssueKind;
  title: string;
  detail?: string;
  fix?: ListingHealthFix;
}

const LISTING_SITES_DONT_RECEIVE = "Listing sites don't receive this yet.";

const KIND_ORDER: Record<ListingHealthIssueKind, number> = {
  must: 0,
  short: 1,
  tip: 2
};

const unitFix = (section: UnitSection): ListingHealthFix => ({
  target: "unit",
  section
});

const propertyFix = (section: PropertySection): ListingHealthFix => ({
  target: "property",
  section
});

const listOf = (items: string[]) =>
  items.length <= 1
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

type Describe = (
  params: ListingHealthIssueParams,
  context: { pmsName: string | null; kind: ListingHealthIssueKind }
) => Omit<ListingHealthLine, "code" | "kind">;

const describePhotos: Describe = (params, { pmsName }) => {
  const has = params.has ?? 0;
  const missing = (params.aim ?? 0) - has;
  const parts = [`Has ${has}; aim for ${params.aim} or more.`];
  if (params.photos_from_property)
    parts.push(
      "The unit has no photos of its own, so listing sites get the property's."
    );
  if (pmsName && params.photos_from_pms)
    parts.push(
      `Add photos in ${pmsName} so they flow into Boom, or upload them here.`
    );
  return {
    title:
      missing > 0 ? `Add at least ${missing} more photos` : "Add more photos",
    detail: parts.join(" "),
    fix: params.photos_from_property
      ? propertyFix(PROPERTY_SECTION.Photos)
      : unitFix(UNIT_SECTION.Photos)
  };
};

const describeDescription: Describe = (params, { pmsName, kind }) => {
  const tooShort = kind === "must";
  const fromProperty = Boolean(params.description_from_property);
  const syncedFrom =
    pmsName && (params.description_synced ?? true) ? pmsName : null;
  const whose = fromProperty ? "The property's description" : "Description";
  const parts = [
    tooShort
      ? `${params.has ?? 0} characters; needs at least ${params.need}, aim for ${params.aim} or more.`
      : `${params.has ?? 0} characters; aim for ${params.aim} or more.`
  ];

  let fix = unitFix(UNIT_SECTION.General);
  if (fromProperty) {
    parts.push(
      syncedFrom
        ? `It syncs from ${syncedFrom}: update it there.`
        : "Listing sites get it for every unit of the property."
    );
    fix = propertyFix(PROPERTY_SECTION.General);
  } else if (syncedFrom)
    parts.push(
      `Syncs from ${syncedFrom}: update it there, or switch Description to Edit manually to change it here (otherwise the next sync replaces your change).`
    );

  return {
    title: tooShort ? `${whose} is too short` : `${whose} is short`,
    detail: parts.join(" "),
    fix
  };
};

const describeAmenities: Describe = (params, { kind }) => {
  if (kind === "must")
    return {
      title: "Add amenities",
      detail:
        "Listing sites look for parking, laundry, heating, cooling and dishwasher.",
      fix: unitFix(UNIT_SECTION.Amenities)
    };
  const missing = params.missing_amenities ?? [];
  const parts = [`Has ${params.has ?? 0}; aim for more than ${params.aim}.`];
  if (missing.length) parts.push(`Missing: ${listOf(missing)}.`);
  return {
    title: "Add amenities",
    detail: parts.join(" "),
    fix: unitFix(UNIT_SECTION.Amenities)
  };
};

const CONTACT_TEXT = {
  both: "email and phone",
  email: "email",
  phone: "phone"
} as const;

const PETS_TEXT = {
  both: "cats and dogs",
  cats: "cats",
  dogs: "dogs"
} as const;

const DESCRIBE: Record<ListingHealthIssueCode, Describe> = {
  photos: describePhotos,
  description: describeDescription,
  amenities: describeAmenities,
  rent: () => ({
    title: "Add the monthly rent",
    fix: unitFix(UNIT_SECTION.RentalInformation)
  }),
  address: () => ({
    title: "Add the street address",
    fix: unitFix(UNIT_SECTION.General)
  }),
  "property-type": () => ({
    title: "Set the property type",
    detail: "Single family or multifamily, on the Property page.",
    fix: propertyFix(PROPERTY_SECTION.General)
  }),
  baths: () => ({
    title: "Add the number of bathrooms",
    fix: unitFix(UNIT_SECTION.Plan)
  }),
  contact: (params) => ({
    title: `Add a contact ${CONTACT_TEXT[params.missing_contact ?? "both"]}`,
    detail: "In Branding on the Property page.",
    fix: propertyFix(PROPERTY_SECTION.Branding)
  }),
  sqft: () => ({
    title: "Add the square footage",
    fix: unitFix(UNIT_SECTION.Plan)
  }),
  deposit: () => ({
    title: "Add the security deposit",
    fix: unitFix(UNIT_SECTION.RentalInformation)
  }),
  "application-fee": () => ({
    title: "Add the application fee",
    detail: "$0 counts, if you don't charge one.",
    fix: unitFix(UNIT_SECTION.RentalInformation)
  }),
  "pet-fee": (params) => ({
    title: "Add a pet fee",
    detail: `This unit allows ${PETS_TEXT[params.pets ?? "both"]}.`,
    fix: propertyFix(PROPERTY_SECTION.Fees)
  }),
  utilities: () => ({
    title: "Say who pays which utilities",
    detail: LISTING_SITES_DONT_RECEIVE,
    fix: unitFix(UNIT_SECTION.LeaseTerms)
  }),
  "lease-terms": () => ({
    title: "Set the lease terms",
    detail: LISTING_SITES_DONT_RECEIVE,
    fix: unitFix(UNIT_SECTION.LeaseTerms)
  }),
  tour: () => ({
    title: "Add a video or 3D tour link",
    detail: "Listings with a tour get more leads.",
    fix: unitFix(UNIT_SECTION.ListingAppearance)
  })
};

export const describeListingHealthIssue = (
  issue: ListingHealthIssue,
  { pmsName }: { pmsName: string | null }
): ListingHealthLine | null => {
  const describe = DESCRIBE[issue.code];
  if (!describe) return null;
  const kind = issue.code === "tour" ? "tip" : issue.kind;
  return {
    code: issue.code,
    kind,
    ...describe(issue.params ?? {}, { pmsName, kind })
  };
};

/** Worst first: must-haves, then items short, then the tour tip. */
export const buildListingHealthLines = (
  health: ListingHealth
): ListingHealthLine[] => {
  const pmsName = health.pms_name ?? null;
  const issues = health.issues ?? [];
  const withTour =
    health.has_tour === false && !issues.some((i) => i.code === "tour")
      ? [...issues, { code: "tour", kind: "tip" } as ListingHealthIssue]
      : issues;

  return withTour
    .map((issue) => describeListingHealthIssue(issue, { pmsName }))
    .filter((line): line is ListingHealthLine => line !== null)
    .map((line, index) => ({ line, index }))
    .sort(
      (a, b) =>
        KIND_ORDER[a.line.kind] - KIND_ORDER[b.line.kind] || a.index - b.index
    )
    .map(({ line }) => line);
};
